import React, { useEffect, useRef, useState } from "react";
import { useStillness } from "../../../components/ui";
// The design team's own artwork, taken in whole rather than rebuilt out of
// markup: each one is a picture of something the brand screen sets up — a
// branded invoice landing in a client's inbox, the meeting apps connected, the
// contact page a client fills in — and nothing in them is read or changed by
// this app.
import brandedPdfArt from "../../../assets/svg/my brand/1.webp";
import integrationsArt from "../../../assets/svg/my brand/2.webp";
import contactPageArt from "../../../assets/svg/my brand/3.webp";

/** One thing the brand screen sets up, as a phone is shown it. */
interface Slide {
  /** Enough to tell the slides apart in a key and a label. */
  id: string;
  art: string;
  /** What the picture shows, for anybody who cannot see it. */
  alt: string;
  /** The headline, in the two inks the design sets it in. */
  lead: string;
  accent: string;
  body: string;
}

/**
 * The three the design leads with, in the order a practitioner meets them:
 * what goes out under their name, what they connect it to, and where the
 * clients come in.
 *
 * Copy, not data — there is nothing behind it to fetch and nothing on this
 * screen to change it.
 */
const SLIDES: Slide[] = [
  {
    id: "branded",
    art: brandedPdfArt,
    alt: "A payment request email on a phone, with the practitioner's own logo at the top and a branded plan PDF attached",
    lead: "Branded",
    accent: "pdfs & emails",
    body: "Every plan, invoice and email goes out with your logo.",
  },
  {
    id: "integrations",
    art: integrationsArt,
    alt: "Google Meet connected and Zoom waiting to be, over a stack of appointments with a Join button on the next one",
    lead: "Integrate on",
    accent: "apps",
    body: "Connect your apps once - then start appointment with clients in a tap.",
  },
  {
    id: "contact-page",
    art: contactPageArt,
    alt: "A practitioner's own contact page open on a phone, asking a new client what they need help with",
    lead: "Your own",
    accent: "contact page",
    body: "Clients share their goals before the first call - straight to you.",
  },
];

/**
 * How long a slide is held before the next one takes its place.
 *
 * Five seconds. A slide is a picture, a headline and one line under it — the
 * picture is most of the reading and it is done at a glance, so this is
 * shorter than the appointment strip's six, which is three lines of detail
 * somebody is scanning for a name.
 */
const SLIDE_MS = 5000;

/**
 * How long the track takes to carry the next slide in, and the curve it does
 * it on. The same pair the appointment strip travels on, so the two carousels
 * in the app move at one speed.
 */
const GLIDE = "transform 450ms cubic-bezier(0.22, 0.61, 0.36, 1)";

/**
 * How far a finger has to travel before it counts as a swipe rather than a
 * press that wandered.
 *
 * 48px — about a thumb's width, and well past the 6px it takes to decide the
 * gesture is horizontal at all. Below it the slide springs back, which is the
 * answer to "I did not mean that".
 */
const SWIPE_MIN = 48;

/** How far the first slide gives when it is pulled towards a slide that is not there. */
const RESIST = 4;

/** Which way a finger is going, once it has gone far enough to say. */
type Axis = "none" | "x" | "y";

/**
 * What a phone is shown of My brand: the three things it sets up, one at a
 * time, with nothing to press.
 *
 * The screen itself cannot be used here — brand details, plans, services, the
 * contact form and the policy documents are all long forms with documents
 * previewed beside them, and the note under this says where to go for them.
 * So the phone gets what it can usefully carry: what the screen is *for*,
 * shown rather than listed, and the honest answer that the setting up happens
 * on a desktop.
 *
 * It moves on its own because nothing on this screen asks to be read in
 * order — it is three equal pictures and somebody looking at them has no
 * reason to reach for anything. It moves by hand as well, which is the part
 * that matters on a phone: a picture that changes under a thumb is a picture
 * a thumb expects to be able to change. Both ways in are here — a swipe and
 * the dots below — and a reader who has asked for less movement gets neither
 * the drift nor the glide, only the dots.
 */
const BrandShowcase: React.FC = () => {
  const [index, setIndex] = useState(0);
  /**
   * Whether a finger is on the strip.
   *
   * Nothing advances under a hand that is holding it — the whole point of a
   * swipe is that the reader has taken the strip over, and a slide leaving on
   * its own timer a moment later would take it straight back off them.
   */
  const [held, setHeld] = useState(false);
  /**
   * How far the track is pulled off its position, in pixels, while a finger
   * is on it. Zero at rest, which is also what says the track may animate
   * again — see the transition below.
   */
  const [drag, setDrag] = useState(0);
  /**
   * Set for the one frame in which the track jumps back to the start without
   * animating — see `rendered` and `handleGlideEnd`.
   */
  const [snapping, setSnapping] = useState(false);
  const still = useStillness();

  /**
   * Where the finger went down and which way it has committed to going.
   *
   * A ref rather than state: it changes on every `touchmove` and nothing on
   * screen is drawn from it, so re-rendering for it would be a render per
   * frame of a gesture to show the same picture.
   */
  const gesture = useRef<{ x: number; y: number; axis: Axis } | null>(null);

  const count = SLIDES.length;
  /** Where the copy of the first slide sits: one past the last real one. */
  const clonePosition = count;

  /**
   * Moves to the next slide, and onto the copy of the first from the last.
   *
   * The same trick the appointment strip turns, and for the same reason: going
   * back to 0 would travel the whole way across every slide in between, which
   * reads as the carousel running backwards rather than coming round.
   * `handleGlideEnd` puts it back on the real first slide once the eye has
   * stopped following it.
   *
   * A `setTimeout` re-armed on every index change rather than one standing
   * `setInterval`, so a slide reached by a dot or a swipe gets the full dwell
   * rather than whatever was left of a tick that began before it.
   */
  useEffect(() => {
    if (held || still || count < 2) return;

    const timer = setTimeout(
      () => setIndex((current) => Math.min(current + 1, clonePosition)),
      SLIDE_MS
    );
    return () => clearTimeout(timer);
  }, [index, held, still, count, clonePosition]);

  /**
   * Lets the track animate again, the frame after it was put back.
   *
   * Two frames rather than one: the first is the one the browser paints the
   * jump in, with the transition off. Turning it back on in the same frame
   * would let the browser coalesce the two changes and animate the jump after
   * all, which is the rewind this exists to avoid.
   */
  useEffect(() => {
    if (!snapping) return;

    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => setSnapping(false));
    });

    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
  }, [snapping]);

  /**
   * The moment the glide onto the copy of the first slide finishes, the track
   * is moved back onto the real one with the animation off. Nothing is seen:
   * the two slides are the same picture drawn the same way.
   *
   * Guarded on the target because `transitionend` bubbles — a colour settling
   * on a dot inside the strip would otherwise be read as the track arriving.
   */
  const handleGlideEnd = (event: React.TransitionEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.propertyName && event.propertyName !== "transform") return;
    if (index !== clonePosition) return;

    setSnapping(true);
    setIndex(0);
  };

  const startSwipe = (event: React.TouchEvent<HTMLDivElement>) => {
    const touch = event.touches[0];
    if (!touch || count < 2) return;

    gesture.current = { x: touch.clientX, y: touch.clientY, axis: "none" };
    setHeld(true);
  };

  /**
   * Follows the finger, once it is clear the finger is going sideways.
   *
   * The axis is decided once, at 6px, and then kept: a thumb travelling down
   * the page wanders a few pixels across on the way, and a strip that took
   * those pixels would snag every scroll past it. Deciding it once is also
   * what stops a gesture switching hands halfway through.
   *
   * Nothing is prevented here — `touch-action: pan-y` on the frame is what
   * tells the browser this strip takes the sideways half of a gesture and
   * leaves the up-and-down half to the page, and it does that before the
   * first event rather than after it.
   */
  const trackSwipe = (event: React.TouchEvent<HTMLDivElement>) => {
    const start = gesture.current;
    const touch = event.touches[0];
    if (!start || !touch) return;

    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;

    if (start.axis === "none") {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
      start.axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
    }
    if (start.axis !== "x") return;

    // There is nothing to the left of the first slide — the track begins
    // there — so a pull that way gives a quarter of itself and springs back.
    // Which is the honest answer: the strip only comes round forwards.
    setDrag(index === 0 && dx > 0 ? dx / RESIST : dx);
  };

  const endSwipe = () => {
    const start = gesture.current;
    gesture.current = null;
    setHeld(false);

    if (start?.axis === "x") {
      if (drag <= -SWIPE_MIN) {
        setIndex((current) => Math.min(current + 1, clonePosition));
      } else if (drag >= SWIPE_MIN) {
        setIndex((current) => Math.max(current - 1, 0));
      }
    }
    // Back to the position, whether that is the one it was pulled off or the
    // next one along. Either way the track animates there from where the
    // finger left it, because `drag` is what was holding the transition off.
    setDrag(0);
  };

  // The track never travels past the copy at the end.
  const at = Math.min(index, clonePosition);
  /** Which slide is actually in view — the copy stands for the first. */
  const showing = at % count;

  /**
   * The slides the track carries: all three, and then the first one again.
   * That last entry is the whole of how this goes round — see the timer above.
   */
  const rendered = count > 1 ? [...SLIDES, SLIDES[0]] : SLIDES;

  return (
    <section
      aria-roledescription="carousel"
      aria-label="What My brand sets up"
      className="flex min-h-0 flex-1 flex-col"
    >
      {/* The frame, which holds still — what moves is the track inside it.
          Full width of the screen rather than of the page, so a slide on its
          way in is cropped by the edge of the phone instead of appearing out
          of the gutter; each slide spends the gutter back as its own padding,
          which is what keeps the artwork lined up with the title above.

          `touch-pan-y` hands the sideways half of a gesture to the handlers
          below and leaves the up-and-down half to the page, so the screen
          still scrolls under a thumb that is not swiping. */}
      <div
        className="-mx-page flex min-h-0 flex-1 touch-pan-y overflow-hidden"
        onTouchStart={startSwipe}
        onTouchMove={trackSwipe}
        onTouchEnd={endSwipe}
        onTouchCancel={endSwipe}
      >
        <div
          // Exactly the width of the frame, and told so twice: a flex item
          // whose own content overflows it is sized from that content unless
          // `min-w-0` says otherwise, and a track four slides wide would make
          // every `-100%` step four times too far.
          className="flex w-full min-w-0"
          onTransitionEnd={handleGlideEnd}
          style={{
            // Whole slides, plus however far a finger has pulled the track off
            // one. The percentage is of the track's own width, so there is
            // nothing to measure and nothing to drift after a rotation.
            transform: `translate3d(calc(${-at * 100}% + ${drag}px), 0, 0)`,
            // Off while a finger is on it — a track that eased towards the
            // thumb would lag behind it — off for the frame it is put back on,
            // and off entirely for a reader who has asked for less movement.
            transition: still || snapping || drag !== 0 ? undefined : GLIDE,
          }}
        >
          {rendered.map((slide, position) => {
            const real = position % count;
            const inView = position === at;

            return (
              <div
                // The copy shares a slide with the first one, so it cannot
                // share its key.
                key={position === clonePosition ? `${slide.id}-again` : slide.id}
                // Every slide is the full width of the frame and refuses to be
                // squeezed, which is what keeps one in view at a time and the
                // `-100%` steps exact.
                className="flex w-full flex-shrink-0 flex-col px-page"
                role="group"
                aria-roledescription="slide"
                aria-label={`${real + 1} of ${count}`}
                // The slides waiting either side are drawn but are not there,
                // so nothing in them is read out or reached by a tab.
                inert={!inView}
                aria-hidden={!inView || undefined}
              >
                {/* The artwork, at the width of the page and whatever height
                    the screen has left once everything else on it has been
                    measured — the headline under it, the dots, and the band at
                    the foot. It took a share of the viewport instead until the
                    sum of those came out taller than the phone and cut the band
                    in half; a fraction cannot know what it is sharing with, and
                    every one that fits a tall phone overflows a short one.

                    So it is the one thing here that gives: it is the largest
                    element by some way and the only one that can lose height
                    without losing anything. `object-contain` is what sizes the
                    picture inside the box it is given rather than cropping it —
                    these are whole compositions with a device in the middle,
                    and one cut off by a box reads as a rendering mistake.

                    The floor is what keeps the page honest on a phone too short
                    for all of it: below that the artwork stops shrinking and the
                    screen scrolls instead, which is the one answer that never
                    hides anything.

                    The picture is laid over the box rather than put inside it,
                    and that is the whole of why this fits. A picture in the flow
                    reports its own height to the column above — a file 378x498
                    wide-to-tall at the width of a phone is 445px of it — and a
                    flex box asked to be "whatever is left" is only ever handed
                    what is left after its own contents have been counted, so the
                    page came out a picture's height too tall and the band fell
                    off the bottom of it. Out of the flow there is nothing to
                    count: the box is the floor, the screen hands it the rest,
                    and `object-contain` fits the whole composition inside
                    whatever that turns out to be — these are whole pictures with
                    a device in the middle, and one cropped by its box reads as a
                    rendering mistake. */}
                <div className="relative w-full min-h-[180px] flex-1">
                  <img
                    src={slide.art}
                    alt={slide.alt}
                    width={378}
                    height={498}
                    className="absolute inset-0 h-full w-full object-contain"
                  />
                </div>

                {/* The headline, in the design's two inks: what the thing is
                    in the deep teal the app titles everything in, and what it
                    does for you in the brand's own. One sentence, so the
                    second half is a `<span>` inside the heading rather than a
                    line of its own. */}
                <h2 className="mt-6 flex-shrink-0 text-center text-[28px] font-semibold leading-[1.2] text-[#00424B]">
                  {slide.lead}{" "}
                  <span className="text-[#00A5B4]">{slide.accent}</span>
                </h2>

                {/* Held to about 20 characters a line of its own accord — the
                    design breaks every one of these over two lines, and a
                    sentence that runs the full width of a wide phone reads as
                    a paragraph rather than a caption. */}
                <p className="mx-auto mt-3 max-w-[21rem] flex-shrink-0 text-center text-[16px] leading-[1.5] text-ink-muted">
                  {slide.body}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Dots rather than arrows: there are three, and a row of dots says how
          many there are as well as where you are. The one showing is a bar
          rather than a filled circle — at this size a dot that differs only in
          colour is not enough to find at a glance, and the bar reads as "here,
          and there is more of it" the way a phone's own page indicators do. */}
      <div className="mt-6 flex flex-shrink-0 items-center justify-center gap-2">
        {SLIDES.map((slide, position) => (
          <button
            key={slide.id}
            type="button"
            aria-label={`Show ${position + 1} of ${count}`}
            // The copy at the end is the first slide, so its dot is the first
            // dot — the row must not go blank as the strip comes round.
            aria-current={position === showing || undefined}
            onClick={() => setIndex(position)}
            className={`h-1.5 rounded-full transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2CB]/40 focus-visible:ring-offset-2 ${
              position === showing
                ? "w-10 bg-[#00BACB]"
                : "w-1.5 bg-[#D6DEE2]"
            }`}
          />
        ))}
      </div>
    </section>
  );
};

export default BrandShowcase;
